'use client';

import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  TransitionChild,
} from '@headlessui/react';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { AiOutlineUserAdd } from 'react-icons/ai';
import { BiDollar } from 'react-icons/bi';
import { BsBoxArrowUpRight } from 'react-icons/bs';
import { HiClipboardList, HiOutlineDocumentReport, HiOutlineClipboardList } from 'react-icons/hi';
import {
  MdClose,
  MdEditCalendar,
  MdLogin,
  MdLogout,
  MdMap,
  MdMenu,
  MdOutlineContactSupport,
  MdOutlineFeedback,
  MdOutlineInbox,
  MdOutlineInsights,
  MdOutlineNotifications,
  MdOutlineSms,
  MdOutlineTimeline,
  MdOutlineSchool,
  MdSupervisorAccount,
} from 'react-icons/md';
import { RiUserSettingsLine } from 'react-icons/ri';
import { TbUsers } from 'react-icons/tb';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { usePortalAccess } from './providers/portalAccess';
import { DarkThemeToggle } from '../lib';
import apiServices from '../services/apiServices';
import {
  GetPortalNotifications,
  GetPortalNotificationSummary,
  MarkPortalNotificationsRead,
  PortalNotification,
} from '../services/dtos';
import { fmtDateTime, TICKET_STAFF_ROLES } from './pages/supportShared';
import {
  RequireAuthenticated,
  RequireUnAuthenticated,
  useAuth,
} from './providers/auth';
import {
  ROLE_ADMIN,
  ROLE_BETA,
  ROLE_OFFICE_PMTS,
  ROLE_STEWARD,
  ROLE_STEWARD_TEXT,
} from './Root';

type NavigationItem = {
  kind?: 'item';
  name: string;
  href: string;
  current: boolean;
  requiresAuth: boolean;
  roles?: string[];
  external?: boolean;
  onClick?: () => void;
  icon?: typeof MdLogin;
  indented?: boolean;
};

type NavLabel = {
  kind: 'label';
  name: string;
  icon?: typeof MdLogin;
  roles?: string[];
};

type NavEntry = NavigationItem | NavLabel;

const navigation = (pathname: string): NavEntry[] => [
  {
    name: 'Login',
    href: '/login',
    current: pathname === '/login',
    requiresAuth: false,
    icon: MdLogin,
  },
  {
    name: 'Register',
    href: '/register',
    current: pathname === '/register',
    requiresAuth: false,
    icon: AiOutlineUserAdd,
  },
  {
    name: 'Our Venues',
    href: 'https://www.google.com/maps/d/u/0/viewer?hl=en&ll=32.754887525686385%2C-117.14119669492595&z=12&mid=1xwQY93-0vYb75F0OtnAV-PX0SsrgPaZe',
    current: false,
    requiresAuth: true,
    external: true,
    icon: MdMap,
  },
  {
    name: 'My Availability',
    href: '/avail',
    current: pathname === '/avail',
    requiresAuth: true,
    icon: MdEditCalendar,
  },
  {
    name: 'Membership Info',
    href: '/info',
    current: pathname === '/info',
    requiresAuth: true,
    icon: RiUserSettingsLine,
  },
  {
    name: 'Job History',
    href: '/jobhist',
    current: pathname === '/jobhist',
    requiresAuth: true,
    icon: HiClipboardList,
  },
  {
    name: 'Wage History',
    href: '/wagehist',
    current: pathname === '/wagehist',
    requiresAuth: true,
    icon: HiOutlineDocumentReport,
  },
  {
    name: 'Office Pmts',
    href: '/dues-admin',
    current: pathname === '/dues-admin',
    requiresAuth: true,
    roles: [ROLE_OFFICE_PMTS, ROLE_ADMIN],
    icon: TbUsers,
  },
  {
    name: 'Timesheet',
    href: '/timesheet',
    current: pathname === '/timesheet',
    requiresAuth: true,
    roles: [ROLE_BETA, ROLE_ADMIN],
    icon: MdOutlineTimeline,
  },
  {
    name: 'Payments',
    href: '/dues',
    current: pathname === '/dues',
    requiresAuth: true,
    icon: BiDollar,
  },
  {
    kind: 'label',
    name: 'Job Steward',
    icon: MdSupervisorAccount,
    roles: [ROLE_STEWARD, ROLE_STEWARD_TEXT, ROLE_ADMIN],
  },
  {
    name: 'Steward Report',
    href: '/steward-report',
    current: pathname === '/steward-report',
    requiresAuth: true,
    icon: HiOutlineDocumentReport,
    indented: true,
    roles: [ROLE_STEWARD, ROLE_ADMIN],
  },
  {
    name: 'Crew List Reports',
    href: '/crew-list',
    current: pathname === '/crew-list',
    requiresAuth: true,
    icon: HiOutlineClipboardList,
    indented: true,
    roles: [ROLE_ADMIN, ROLE_STEWARD],
  },
  {
    name: 'Text Members On Job',
    href: '/text-members-on-job',
    current: pathname === '/text-members-on-job',
    requiresAuth: true,
    roles: [ROLE_STEWARD_TEXT, ROLE_ADMIN],
    icon: MdOutlineSms,
    indented: true,
  },
  {
    name: 'Skills',
    href: '/skills',
    current: pathname === '/skills',
    requiresAuth: true,
    roles: [ROLE_ADMIN],
    icon: MdOutlineSchool,
  },
  {
    name: 'Role Admin',
    href: '/role-admin',
    current: pathname === '/role-admin',
    requiresAuth: true,
    roles: [ROLE_ADMIN],
    icon: RiUserSettingsLine,
  },
  {
    name: 'Union Documents',
    href: '/docs',
    current: pathname === '/docs' || pathname.startsWith('/docs/'),
    requiresAuth: true,
    icon: HiOutlineDocumentReport,
  },
  {
    name: 'Document Admin',
    href: '/doc-admin',
    current: pathname === '/doc-admin',
    requiresAuth: true,
    // DocumentAdmin holders; Admin passes via the hasPortalRole bypass
    roles: ['DocumentAdmin'],
    icon: HiOutlineDocumentReport,
  },
  {
    name: 'Get Support',
    href: '/help',
    current: pathname === '/help' || pathname.startsWith('/help/'),
    requiresAuth: true,
    icon: MdOutlineContactSupport,
  },
  {
    name: 'Support Queue',
    href: '/help/queue',
    current: pathname === '/help/queue' || pathname.startsWith('/help/board') ||
      pathname.startsWith('/help/snippets') || pathname.startsWith('/help/delegations') ||
      pathname.startsWith('/help/member'),
    requiresAuth: true,
    // every staff lane (T5): the five queues + the BR/ABR escalation lanes;
    // delegated lanes ride /portal/access; Admin via the hasPortalRole bypass
    roles: TICKET_STAFF_ROLES,
    icon: MdOutlineInbox,
  },
  {
    name: 'Leadership Dashboard',
    href: '/help/dashboard',
    current: pathname === '/help/dashboard',
    requiresAuth: true,
    // DashboardViewer holders; Admin via the hasPortalRole bypass (S1)
    roles: ['DashboardViewer'],
    icon: MdOutlineInsights,
  },
  {
    name: 'Feedback',
    href: '/feedback',
    current: pathname === '/feedback',
    requiresAuth: true,
    icon: MdOutlineFeedback,
  },
];

function DashboardLayout({ children }: React.PropsWithChildren<{}>) {
  const auth = useAuth();
  const { pathname } = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // T2 bell: one poller for both bell placements (desktop sidebar + mobile
  // bar). 60s cadence per the plan — no SSE in v1.
  const [notifCount, setNotifCount] = useState(0);
  useEffect(() => {
    if (!auth.isLoggedIn) {
      setNotifCount(0);
      return;
    }
    const refresh = () =>
      apiServices.client
        .get(new GetPortalNotificationSummary())
        .then((r) => setNotifCount(r.unreadCount))
        .catch(() => {});
    refresh();
    const timer = setInterval(refresh, 60_000);
    return () => clearInterval(timer);
  }, [auth.isLoggedIn]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [pathname]);

  return (
    <>
      {sidebarOpen && (
        <Dialog
          open={sidebarOpen}
          onClose={setSidebarOpen}
          className="relative z-50 lg:hidden"
        >
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-gray-900/80 dark:bg-gray-800/70 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
          />

          <div className="fixed inset-0 flex">
            <DialogPanel className="relative animate-in slide-in-from-left-full mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:slide-out-to-left">
              <TransitionChild>
                <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
                  <button
                    type="button"
                    onClick={() => setSidebarOpen(false)}
                    className="-m-2.5 p-2.5"
                  >
                    <span className="sr-only">Close sidebar</span>
                    <MdClose
                      aria-hidden="true"
                      className="h-6 w-6 text-white"
                    />
                  </button>
                </div>
              </TransitionChild>
              {/* Sidebar component, swap this element with another sidebar if you like */}
              <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white dark:bg-gray-900 px-6 pb-2">
                <div className="flex h-16 shrink-0 items-center">
                <div  className="bg-white border-b dark:bg-gray-900 dark:border-gray-700">IATSE 122 Portal</div>
                  <div className="flex flex-1 justify-end">
                    <DarkThemeToggle />
                  </div>
                </div>
                <nav className="flex flex-1 flex-col">
                  <ul role="list" className="flex flex-1 flex-col gap-y-7">
                    <li>
                      <ul role="list" className="-mx-2 space-y-1">
                        {navigation(pathname).map((entry) =>
                          entry.kind === 'label'
                            ? <NavLabelItem key={entry.name} entry={entry} />
                            : <NavItem key={entry.name} item={entry} />
                        )}
                      </ul>
                    </li>
                    {auth.isLoggedIn && (
                      <li className="-mx-6 mt-auto">
                        <button
                          className="flex w-full items-center text-red-500 gap-x-4 px-6 py-3 text-sm font-semibold leading-6  hover:bg-gray-50 dark:hover:bg-gray-700"
                          onClick={auth.signOut}
                        >
                          <MdLogout className=" w-6 h-6" />
                          Logout
                        </button>
                      </li>
                    )}
                  </ul>
                </nav>
              </div>
            </DialogPanel>
          </div>
        </Dialog>
      )}

      {/* Static sidebar for desktop */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
        {/* Sidebar component, swap this element with another sidebar if you like */}
        <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r dark:border-none border-gray-200 bg-white dark:bg-gray-900 px-6">
          <div className="flex h-16 shrink-0 items-center">
          <div   className="text text-gray-700 uppercase bg-gray-50 dark:bg-gray-900 dark:text-gray-400">IATSE 122 Portal</div>
            <div className="ml-auto -mr-3 flex items-center gap-1">
              <NotificationBell
                count={notifCount}
                onCount={setNotifCount}
                panelClass="fixed left-4 top-14"
              />
              <DarkThemeToggle />
            </div>
          </div>
          <nav className="flex flex-1 flex-col">
            <ul role="list" className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul role="list" className="-mx-2 space-y-1">
                  {navigation(pathname).map((entry) =>
                    entry.kind === 'label'
                      ? <NavLabelItem key={entry.name} entry={entry} />
                      : <NavItem key={entry.name} item={entry} />
                  )}
                </ul>
              </li>

              {auth.isLoggedIn && (
                <li className="-mx-6 mt-auto ">
                  <button
                    className="flex w-full items-center text-red-500 gap-x-4 px-6 py-3 text-sm font-semibold leading-6  hover:bg-gray-50 dark:hover:bg-gray-700"
                    onClick={auth.signOut}
                  >
                    <MdLogout className=" w-6 h-6" />
                    Logout
                  </button>
                </li>
              )}
            </ul>
          </nav>
        </div>
      </div>

      <div className="sticky top-0 z-40 flex items-center gap-x-6 dark:bg-gray-900 bg-white px-4 py-4 shadow-sm sm:px-6 lg:hidden">
        <button
          type="button"
          onClick={() => setSidebarOpen(true)}
          className="-m-2.5 p-2.5 text-gray-700 dark:text-gray-100 lg:hidden"
        >
          <span className="sr-only">Open sidebar</span>
          <MdMenu aria-hidden="true" className="h-6 w-6" />
        </button>
        <div className="flex-1 text-sm font-semibold leading-6 text-gray-900 dark:text-white">
          Dashboard
        </div>
        <div className="-my-2">
        <div   className="text text-gray-700 uppercase bg-gray-50 dark:bg-gray-900 dark:text-gray-400">IATSE 122 Portal</div>
          <DarkThemeToggle />
        </div>
        <NotificationBell
          count={notifCount}
          onCount={setNotifCount}
          panelClass="fixed right-2 top-14"
        />
        {auth.isLoggedIn && (
          <button onClick={auth.signOut}>
            <MdLogout className=" w-6 h-6 text-red-500" />
          </button>
        )}
      </div>

      {/* minmax(0,1fr): without it the implicit grid column has a min-content
          floor, so wide tables punch through their overflow-x wrappers and
          mobile Chrome expands the whole layout viewport (Kevin caught this
          on /doc-admin; /help/queue had it too) */}
      <main className="py-10 grid grid-cols-[minmax(0,1fr)] lg:pl-[20rem] flex-1 px-6 bg-white dark:bg-gray-800">
        {children}
      </main>
    </>
  );
}

function NavItem({ item }: { item: NavigationItem }) {
  const { hasPortalRole } = usePortalAccess();
  const render = () => (
    <div
      className={classNames(
        item.current
          ? 'bg-gray-50 dark:bg-gray-700 text-indigo-600 dark:text-white'
          : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 hover:text-indigo-600 dark:hover:text-white dark:hover:bg-gray-800',
        item.indented ? 'ml-7 pl-2 border-l border-gray-200 dark:border-gray-700' : '',
        'group items-center flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6'
      )}
    >
      {item.icon && (
        <item.icon
          aria-hidden="true"
          className={classNames(
            item.current
              ? 'text-indigo-600 dark:text-gray-200'
              : 'text-gray-400 dark:text-gray-400 dark:group-hover:text-gray-300 group-hover:text-indigo-600 ',
            'h-6 w-6 shrink-0'
          )}
        />
      )}
      {item.name}
      {item.external && (
        <div className="flex-1 flex justify-end">
          <BsBoxArrowUpRight className="text-inherit w-4 h-4" />
        </div>
      )}
    </div>
  );
  if (item.roles && !hasPortalRole(...item.roles)) return null;

  const Comp = item.requiresAuth
    ? RequireAuthenticated
    : RequireUnAuthenticated;

  const children = (
    <>
      {item.requiresAuth && (
        <RequireAuthenticated redirect={false}>
          {render()}
        </RequireAuthenticated>
      )}
      {!item.requiresAuth && (
        <RequireUnAuthenticated redirect={false}>
          {render()}
        </RequireUnAuthenticated>
      )}
    </>
  );
  return (
    <Comp key={item.name} redirect={false}>
      <li>
        {item.external ? (
          <a href={item.href} target="_blank">
            {children}
          </a>
        ) : (
          <Link to={item.href}>{children}</Link>
        )}
      </li>
    </Comp>
  );
}

// The SPA owns notification routing — the backend supplies domain references
// (ticketId, documentId), never URLs (Kevin, 2026-07-31). Future notification
// kinds (announcements) add their case here.
const notificationRoute = (n: PortalNotification): string | null =>
  n.ticketId
    ? `/help/ticket/${n.ticketId}`
    : n.documentId
    ? `/docs/${n.documentId}`
    : null;

// T2 notification bell. The dropdown panel is position:fixed (via panelClass)
// so the sidebar's overflow-y-auto can't clip it; a transparent backdrop
// closes it on any outside click. Clicking an item marks it read and follows
// its route; the returned summary keeps the badge honest.
function NotificationBell({
  count,
  onCount,
  panelClass,
}: {
  count: number;
  onCount: (n: number) => void;
  panelClass: string;
}) {
  const auth = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<PortalNotification[]>([]);
  const [loading, setLoading] = useState(false);

  if (!auth.isLoggedIn) return null;

  const openPanel = () => {
    setOpen(true);
    setLoading(true);
    apiServices.client
      .get(new GetPortalNotifications())
      .then((data: PortalNotification[]) => setItems(data))
      .catch(() => {})
      .then(() => setLoading(false));
  };

  const markRead = (ids?: number[]) =>
    apiServices.client
      .post(new MarkPortalNotificationsRead(ids ? { ids } : { all: true }))
      .then((r) => onCount(r.unreadCount))
      .catch(() => {});

  const openItem = (n: PortalNotification) => {
    setOpen(false);
    if (!n.readAtUtc) markRead([n.id]);
    const route = notificationRoute(n);
    if (route) navigate(route);
  };

  const markAll = () => {
    markRead();
    setItems((cur) =>
      cur.map((n) => ({ ...n, readAtUtc: n.readAtUtc || new Date().toISOString() }))
    );
  };

  return (
    <>
      <button
        type="button"
        onClick={() => (open ? setOpen(false) : openPanel())}
        className="relative p-1.5 text-gray-500 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-white"
        aria-label="Notifications"
      >
        <MdOutlineNotifications className="h-6 w-6" />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className={classNames(
              panelClass,
              'z-50 flex max-h-[70vh] w-80 max-w-[calc(100vw-1rem)] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900'
            )}
          >
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5 dark:border-gray-700">
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                Notifications
              </span>
              {count > 0 && (
                <button
                  onClick={markAll}
                  className="text-xs font-medium text-indigo-600 hover:underline dark:text-indigo-400"
                >
                  Mark all read
                </button>
              )}
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading && (
                <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                  Loading…
                </div>
              )}
              {!loading && items.length === 0 && (
                <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                  You're all caught up.
                </div>
              )}
              {!loading &&
                items.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => openItem(n)}
                    className={classNames(
                      'block w-full border-b border-gray-50 px-4 py-2.5 text-left last:border-0 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800',
                      !n.readAtUtc ? 'bg-indigo-50/50 dark:bg-gray-800/60' : ''
                    )}
                  >
                    <div className="flex items-start gap-2">
                      {!n.readAtUtc && (
                        <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-indigo-600" />
                      )}
                      <div className="min-w-0">
                        <div
                          className={classNames(
                            'text-sm break-words',
                            !n.readAtUtc
                              ? 'font-semibold text-gray-900 dark:text-white'
                              : 'text-gray-700 dark:text-gray-300'
                          )}
                        >
                          {n.title}
                        </div>
                        <div className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">
                          {fmtDateTime(n.createdAtUtc)}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
            </div>
          </div>
        </>
      )}
    </>
  );
}

function NavLabelItem({ entry }: { entry: NavLabel }) {
  const auth = useAuth();
  const { hasPortalRole } = usePortalAccess();
  if (!auth.isLoggedIn) return null;
  if (entry.roles && !hasPortalRole(...entry.roles)) return null;
  return (
    <li className="flex items-center gap-x-3 px-2 pt-3 pb-0.5">
      {entry.icon && (
        <entry.icon
          aria-hidden="true"
          className="h-5 w-5 shrink-0 text-gray-400 dark:text-gray-500"
        />
      )}
      <span className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
        {entry.name}
      </span>
    </li>
  );
}

export { DashboardLayout };
